import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { URGENCY_LEVELS, urgencyColor, urgencyLabel, urgencyOptionLabel } from '../urgency';
import {
  ArrowLeft, ArrowRight, Check, Flame, Pencil,
  Settings as SettingsIcon, Upload,
} from 'lucide-react';
import { jobsApi, candidatesApi, teamApi } from '../../../shared/services/api';
import RankedCandidates from '../components/RankedCandidates';
import ResumeReviewModal from '../components/ResumeReviewModal';
import RichTextEditor, { plainTextToHtml } from '../../../shared/components/RichTextEditor';
import { useAlert } from '../../../shared/contexts/AlertContext';
import { useAuth } from '../../auth';
import { canWorkJob } from '../../../shared/utils/permissions';
import { CURRENCIES, DEFAULT_CURRENCY, formatBudget } from '../../../shared/utils/budget';

const SCORE_COLOR = (score) => {
  if (score >= 75) return 'var(--green)';
  if (score >= 55) return 'var(--accent)';
  if (score >= 35) return 'var(--yellow)';
  return 'var(--red)';
};


export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { user } = useAuth();
  // A team lead hands their team's jobs to a recruiter, or keeps them.
  const mayAssign = user?.role === 'team_lead';
  const [assignable, setAssignable] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [job, setJob] = useState(null);
  // The person the job is assigned to works it; everyone else views it.
  const mayRecruit = canWorkJob(user, job);
  const [candidates, setCandidates] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [scoring, setScoring] = useState(false);

  // Pipeline settings state
  const [thresholds, setThresholds] = useState({
    resume_score_threshold: 75,
    interview_score_threshold: 60,
    shortlist_size: 10,
    resume_weight: 0.4,
    interview_weight: 0.6,
  });
  const [savingThresholds, setSavingThresholds] = useState(false);



  // Bulk archive state
  const [archiving, setArchiving] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [review, setReview] = useState(null);          // { rows, skipped } | null
  const [reviewSaving, setReviewSaving] = useState(false);

  // Overview edit mode
  const [editingOverview, setEditingOverview] = useState(false);
  const [editedOverview, setEditedOverview] = useState({});
  const [savingOverview, setSavingOverview] = useState(false);
  const [newSkill, setNewSkill] = useState('');

  const fileRef = useRef();

  useEffect(() => {
    Promise.all([jobsApi.get(id), candidatesApi.list({ job_id: id })])
      .then(([j, c]) => {
        setJob(j);
        setCandidates(c);
        setThresholds({
          resume_score_threshold: j.resume_score_threshold ?? 75,
          interview_score_threshold: j.interview_score_threshold ?? 60,
          shortlist_size: j.shortlist_size ?? 10,
          resume_weight: j.resume_weight ?? 0.4,
          interview_weight: j.interview_weight ?? 0.6,
        });
      })
      .catch(() => navigate('/jobs'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (tab === 'overview') {
      // Re-fetch to pick up any chat-agent edits (e.g. score weight changes)
      jobsApi.get(id).then(j => {
        setJob(j);
        setThresholds(t => ({
          ...t,
          resume_weight: j.resume_weight ?? t.resume_weight,
          interview_weight: j.interview_weight ?? t.interview_weight,
          resume_score_threshold: j.resume_score_threshold ?? t.resume_score_threshold,
          interview_score_threshold: j.interview_score_threshold ?? t.interview_score_threshold,
        }));
      }).catch(() => {});
    }
  }, [tab, id]); // eslint-disable-line

  // Upload parses only. Scoring waits for the review step below, so a missed
  // email or a mangled name is caught before the AI judges the candidate.
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const result = await candidatesApi.uploadResumes(id, files, 'Upload', false);
      const rows = result.results || [];
      const parsed  = rows.filter(r => r.status === 'success');
      const skipped = rows.filter(r => r.status !== 'success');

      if (parsed.length) {
        setReview({ rows: parsed, skipped });
      } else {
        const dups = skipped.filter(r => r.status === 'duplicate');
        const restored = skipped.filter(r => r.status === 'restored');
        const lines = [];
        if (restored.length) lines.push(`${restored.length} previously hidden candidate${restored.length === 1 ? '' : 's'} restored to the list.`);
        if (dups.length) lines.push(`${dups.length} duplicate file${dups.length === 1 ? '' : 's'} skipped.`);
        if (!lines.length) lines.push('No new candidates were added.');
        showAlert({
          type: restored.length ? 'success' : 'warning',
          title: restored.length ? 'Resumes uploaded' : 'No new candidates',
          message: lines.join('\n'),
        });
        const updated = await candidatesApi.list({ job_id: id });
        setCandidates(Array.isArray(updated) ? updated : []);
      }
    } catch (e) {
      showAlert({
        type: 'error',
        title: 'Upload failed',
        message: e.message || 'Something went wrong. Please try again.',
      });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  /** Save any corrections, then score. One candidate failing does not stop the rest. */
  const handleConfirmReview = async (rows) => {
    setReviewSaving(true);
    const original = Object.fromEntries((review?.rows || []).map(r => [r.candidate_id, r]));
    let scored = 0;
    const failed = [];

    for (const row of rows) {
      try {
        const before = original[row.candidate_id] || {};
        if (before.name !== row.name || before.email !== row.email || before.phone !== row.phone) {
          await candidatesApi.updateContact(row.candidate_id, {
            name: row.name, email: row.email, phone: row.phone,
          });
        }
        await candidatesApi.scoreResume(row.candidate_id);
        scored += 1;
      } catch (err) {
        failed.push(`${row.name || row.filename}: ${err.message || 'scoring failed'}`);
      }
    }

    setReviewSaving(false);
    setReview(null);

    const lines = [`${scored} candidate${scored === 1 ? '' : 's'} added and scored.`];
    if (review?.skipped?.length) {
      lines.push(`${review.skipped.length} file${review.skipped.length === 1 ? '' : 's'} skipped.`);
    }
    if (failed.length) lines.push(`Could not score: ${failed.join('; ')}`);
    showAlert({
      type: failed.length ? 'warning' : 'success',
      title: failed.length ? 'Added with problems' : 'Resumes added',
      message: lines.join('\n'),
    });

    const updated = await candidatesApi.list({ job_id: id });
    setCandidates(Array.isArray(updated) ? updated : []);
  };

  /**
   * Cancelling leaves the parsed candidates on the job, unscored. The files
   * are already uploaded and throwing them away would mean re-uploading; an
   * unscored candidate is visible in the list and "AI Score All" picks them up.
   */
  const handleCancelReview = async () => {
    const count = review?.rows?.length || 0;
    setReview(null);
    const updated = await candidatesApi.list({ job_id: id });
    setCandidates(Array.isArray(updated) ? updated : []);
    if (count) {
      showAlert({
        type: 'info',
        title: 'Left unscored',
        message: `${count} candidate${count === 1 ? '' : 's'} added but not scored. Use "AI Score All" when you are ready.`,
      });
    }
  };

  const handleBulkScore = async () => {
    setScoring(true);
    try {
      const result = await candidatesApi.bulkScore(id);
      const scored = result.results.filter(r => r.status === 'scored').length;
      showAlert({ type: 'success', title: 'Resumes scored', message: `Scored ${scored} candidates.` });
      const updated = await candidatesApi.list({ job_id: id });
      setCandidates(updated);
    } catch (e) {
      showAlert({ type: 'error', title: 'Could not score resumes', message: e.message || 'Something went wrong. Please try again.' });
    } finally {
      setScoring(false);
    }
  };

  // window.confirm and window.alert are blocked inside the embedded browser —
  // they return false without showing anything, so the button looked dead.
  // Confirmation is an in-app modal instead.
  const handleBulkArchive = async () => {
    setConfirmArchive(false);
    setArchiving(true);
    try {
      const result = await candidatesApi.bulkArchive(id);
      showAlert({
        type: 'success',
        title: 'Candidates archived',
        message: `Archived ${result.archived_count} candidate${result.archived_count === 1 ? '' : 's'} below the threshold.`,
      });
      const updated = await candidatesApi.list({ job_id: id });
      setCandidates(updated);
    } catch (e) {
      showAlert({ type: 'error', title: 'Bulk archive failed', message: e.message });
    } finally {
      setArchiving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    await jobsApi.update(id, { status: newStatus });
    setJob(prev => ({ ...prev, status: newStatus }));
  };

  useEffect(() => {
    if (!mayAssign) return;
    teamApi.assignableRecruiters().then(setAssignable).catch(() => setAssignable([]));
  }, [mayAssign]);

  const handleAssign = async (assignedToId) => {
    setAssigning(true);
    try {
      const updated = await jobsApi.assign(id, assignedToId);
      setJob(j => ({ ...j, assigned_to_id: updated.assigned_to_id, assigned_to: updated.assigned_to }));
      showAlert({
        type: 'success',
        message: updated.assigned_to?.id === user?.id
          ? 'The job is now with you.'
          : `Assigned to ${updated.assigned_to?.name}. An email was sent to them.`,
      });
    } catch (e) {
      showAlert({ type: 'error', message: e.message });
    }
    setAssigning(false);
  };

  const handleStartEditOverview = () => {
    setEditedOverview({
      title: job.title || '',
      city: job.city || '',
      country: job.country || '',
      work_location: job.work_location || 'On-site',
      min_experience_years: job.min_experience_years || '',
      budget_min: job.budget_min || '',
      budget_max: job.budget_max || '',
      budget_currency: job.budget_currency || DEFAULT_CURRENCY,
      employment_type: job.employment_type || 'Full-time',
      urgency: job.urgency || 'Medium',
      required_skills: [...(job.required_skills || [])],
      job_description: job.job_description || '',
    });
    setEditingOverview(true);
  };

  const handleSaveOverview = async () => {
    setSavingOverview(true);
    try {
      await jobsApi.update(id, editedOverview);
      const updated = await jobsApi.get(id);
      setJob(updated);
      setEditingOverview(false);
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setSavingOverview(false);
    }
  };

  const handleSaveThresholds = async () => {
    setSavingThresholds(true);
    try {
      await jobsApi.update(id, thresholds);
      const updated = await jobsApi.get(id);
      setJob(updated);
      alert('Pipeline settings saved');
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setSavingThresholds(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
      <div className="spinner" style={{ width: 28, height: 28 }} />
    </div>
  );

  const scored = candidates.filter(c => c.resume_score !== null);
  const threshold = thresholds.resume_score_threshold;
  const passedThreshold = candidates.filter(c => c.resume_score >= threshold);
  const jobActive = job.status === 'Active';
  const jobLockHint = jobActive ? undefined : `This job is ${job.status}. Set it to Active to upload resumes.`;

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
            <Link to="/jobs" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>Jobs</Link> / {job.title}
          </div>
          <div className="page-title">{job.title}</div>
          {job.job_code && (
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', marginTop: 4 }}>
              Job Code: <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>{job.job_code}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Inline status changer */}
            {!mayRecruit ? (
              <span className={`badge ${job.status === 'Active' ? 'badge-green' : job.status === 'Closed' ? 'badge-red' : job.status === 'Paused' ? 'badge-yellow' : 'badge-gray'}`}>{job.status}</span>
            ) : (
            <select
              value={job.status}
              onChange={e => handleStatusChange(e.target.value)}
              style={{
                fontSize: 11, fontWeight: 700, padding: '3px 8px',
                borderRadius: 6, border: '1px solid var(--border)',
                background: job.status === 'Active' ? 'rgba(63,185,80,0.12)' :
                             job.status === 'Paused' ? 'rgba(210,153,34,0.12)' :
                             job.status === 'Closed' ? 'rgba(239,68,68,0.12)' : 'var(--bg-surface)',
                color: job.status === 'Active' ? 'var(--green)' :
                        job.status === 'Paused' ? 'var(--yellow)' :
                        job.status === 'Closed' ? 'var(--red)' : 'var(--text-muted)',
                cursor: 'pointer', outline: 'none',
              }}
            >
              <option value="Active">Active</option>
              <option value="Draft">Draft</option>
              <option value="Paused">Paused</option>
              <option value="Closed">Closed</option>
            </select>
            )}
            <span className="badge badge-blue">{job.employment_type}</span>
            {job.urgency && (
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 8,
                color: urgencyColor(job.urgency),
                background: `${urgencyColor(job.urgency)}1a`,
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
                <Flame size={11} strokeWidth={2.2} />
                {urgencyLabel(job.urgency)}
              </span>
            )}
            {job.city && <span className="tag">{job.city}, {job.country}</span>}
            {/* Who posted the job is fixed: its candidates, interviews and the
                team that can see them all follow from it. */}
            {job.created_by && (
              <span className="tag" title={job.created_by.email} style={{ gap: 4 }}>
                <span>Created by</span><strong>{job.created_by.name}</strong>
                {job.created_by.role === 'team_lead' && <span>(Team Lead)</span>}
              </span>
            )}
            {mayAssign && (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                Assigned to
                <select
                  value={job.assigned_to_id === user?.id ? '' : (job.assigned_to_id || '')}
                  disabled={assigning}
                  onChange={e => handleAssign(e.target.value)}
                  style={{
                    fontSize: 12, fontWeight: 600, padding: '3px 6px', borderRadius: 6,
                    border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)',
                  }}
                >
                  <option value="">Myself ({user?.name})</option>
                  {assignable.map(r => <option key={r.id} value={r.id}>{r.name} ({r.email})</option>)}
                </select>
              </label>
            )}
            {!mayRecruit && !mayAssign && (
              <span className="tag" title={job.assigned_to?.email} style={{ gap: 4 }}>
                {job.assigned_to ? <>
                  <span>Assigned to</span><strong>{job.assigned_to.name}</strong>
                  <span>
                    ({job.assigned_to.email})
                    {job.assigned_to.team_lead ? ` · TL ${job.assigned_to.team_lead.name}` : ''}
                    {job.assigned_to.manager ? ` · Manager ${job.assigned_to.manager.name}` : ''}
                  </span>
                </> : 'Assigned to: nobody (admin only)'}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {review && (
            <ResumeReviewModal
              rows={review.rows}
              skipped={review.skipped}
              saving={reviewSaving}
              onCancel={handleCancelReview}
              onConfirm={handleConfirmReview}
            />
          )}
          {mayRecruit && <>
          <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.doc" style={{ display: 'none' }} onChange={handleUpload} />
          <button className="btn btn-secondary" onClick={() => jobActive && fileRef.current?.click()} disabled={!jobActive || uploading} title={jobLockHint}>
            {uploading ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Uploading...</> : <><Upload size={14} strokeWidth={2} /> Upload Resumes</>}
          </button>
          <button className="btn btn-primary" onClick={handleBulkScore} disabled={!jobActive || scoring || candidates.length === 0} title={jobLockHint}>
            {scoring ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Scoring...</> : 'AI Score All'}
          </button>
          </>}
        </div>
      </div>

      {!jobActive && mayRecruit && (
        <div style={{
          background: 'rgba(210,153,34,0.08)', border: '1px solid rgba(210,153,34,0.35)',
          borderRadius: 10, padding: '10px 16px', marginBottom: 16,
          fontSize: 13, color: 'var(--yellow)',
        }}>
          This job is <strong>{job.status}</strong>. Set it to Active to upload resumes or score candidates.
        </div>
      )}

      <div className="tabs">
        {[
          ['overview', 'Overview'],
          ['candidates', `Candidates (${candidates.length})`],
          ['settings', 'Pipeline Settings'],
        ].map(([key, label]) => (
          <button key={key} className={`tab-btn ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {/* ─── OVERVIEW ─── */}
      {tab === 'overview' && (
        <div className="grid-2">
          {/* ── Job Details Card ── */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div className="section-title" style={{ margin: 0 }}>Job Details</div>
              {!mayRecruit ? null : !editingOverview ? (
                <button className="btn btn-secondary btn-sm" onClick={handleStartEditOverview}><Pencil size={12} strokeWidth={2} /> Edit</button>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary btn-sm" onClick={handleSaveOverview} disabled={savingOverview}>
                    {savingOverview ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Saving…</> : <><Check size={12} strokeWidth={2.5} /> Save</>}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditingOverview(false)}>Cancel</button>
                </div>
              )}
            </div>

            {!editingOverview ? (
              <>
                {[
                  ['Job code', job.job_code || '—'],
                  ['Title', job.title],
                  ['Location', [job.city, job.country].filter(Boolean).join(', ') || '—'],
                  ['Work Mode', job.work_location || '—'],
                  ['Min Experience', job.min_experience_years ? `${job.min_experience_years} years` : '—'],
                  ['Budget', formatBudget(job, '—')],
                  ['Employment Type', job.employment_type || '—'],
                  ['Urgency', job.urgency ? urgencyLabel(job.urgency) : '—'],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{k}</span>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{v}</span>
                  </div>
                ))}
                <div className="mt-4">
                  <div className="section-title">Required Skills</div>
                  <div className="tags-wrap">
                    {(job.required_skills || []).map(s => <span key={s} className="tag" style={{ color: 'var(--accent)' }}>{s}</span>)}
                  </div>
                </div>
                {job.job_description && (
                  <div style={{ marginTop: 16 }}>
                    <div className="section-title">Job Description</div>
                    {/* The description is rich text now. It is sanitised on the
                        server before it is stored, so what comes back can only
                        contain the allowlisted formatting tags. */}
                    <div
                      className="jd-rendered"
                      style={{ maxHeight: 220, overflowY: 'auto' }}
                      dangerouslySetInnerHTML={{ __html: plainTextToHtml(job.job_description) }}
                    />
                  </div>
                )}
              </>
            ) : (
              /* ── Edit mode ── */
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { label: 'Job Title', key: 'title', type: 'text' },
                  { label: 'City', key: 'city', type: 'text' },
                  { label: 'Country', key: 'country', type: 'text' },
                  { label: 'Min Experience (years)', key: 'min_experience_years', type: 'number' },
                  { label: 'Annual Budget Min', key: 'budget_min', type: 'number' },
                  { label: 'Annual Budget Max', key: 'budget_max', type: 'number' },
                ].map(({ label, key, type }) => (
                  <div key={key}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 4 }}>{label}</label>
                    <input
                      type={type}
                      value={editedOverview[key] ?? ''}
                      onChange={e => setEditedOverview(o => ({ ...o, [key]: type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value }))}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', fontSize: 13, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}
                    />
                  </div>
                ))}

                {/* What the two budget numbers are counted in */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 4 }}>Currency</label>
                  <select value={editedOverview.budget_currency || DEFAULT_CURRENCY} onChange={e => setEditedOverview(o => ({ ...o, budget_currency: e.target.value }))}
                    style={{ width: '100%', padding: '7px 10px', fontSize: 13, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}>
                    {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                </div>

                {/* Work Mode */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 4 }}>Work Mode</label>
                  <select value={editedOverview.work_location || 'On-site'} onChange={e => setEditedOverview(o => ({ ...o, work_location: e.target.value }))}
                    style={{ width: '100%', padding: '7px 10px', fontSize: 13, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}>
                    {['On-site', 'Remote', 'Hybrid'].map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>

                {/* Employment Type */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 4 }}>Employment Type</label>
                  <select value={editedOverview.employment_type || 'Full-time'} onChange={e => setEditedOverview(o => ({ ...o, employment_type: e.target.value }))}
                    style={{ width: '100%', padding: '7px 10px', fontSize: 13, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}>
                    {['Full-time', 'Part-time', 'Contract', 'Internship', 'Freelance'].map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>

                {/* Urgency */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 4 }}>Urgency</label>
                  <select value={editedOverview.urgency || 'Medium'} onChange={e => setEditedOverview(o => ({ ...o, urgency: e.target.value }))}
                    style={{ width: '100%', padding: '7px 10px', fontSize: 13, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}>
                    {URGENCY_LEVELS.map(l => (
                      <option key={l.value} value={l.value}>{urgencyOptionLabel(l.value)}</option>
                    ))}
                  </select>
                </div>

                {/* Required Skills */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 6 }}>Required Skills</label>
                  <div className="tags-wrap" style={{ marginBottom: 8 }}>
                    {(editedOverview.required_skills || []).map(s => (
                      <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 12, background: 'rgba(88,166,255,0.1)', border: '1px solid rgba(88,166,255,0.3)', fontSize: 12, color: 'var(--accent)', fontWeight: 500 }}>
                        {s}
                        <button onClick={() => setEditedOverview(o => ({ ...o, required_skills: o.required_skills.filter(x => x !== s) }))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 13, padding: 0, lineHeight: 1 }}>×</button>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      value={newSkill}
                      onChange={e => setNewSkill(e.target.value)}
                      onKeyDown={e => {
                        if ((e.key === 'Enter' || e.key === ',') && newSkill.trim()) {
                          e.preventDefault();
                          const s = newSkill.trim();
                          if (!editedOverview.required_skills?.includes(s)) {
                            setEditedOverview(o => ({ ...o, required_skills: [...(o.required_skills || []), s] }));
                          }
                          setNewSkill('');
                        }
                      }}
                      placeholder="Type skill, press Enter"
                      style={{ flex: 1, padding: '6px 10px', fontSize: 12, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}
                    />
                    <button className="btn btn-ghost btn-sm" onClick={() => {
                      const s = newSkill.trim();
                      if (s && !editedOverview.required_skills?.includes(s)) {
                        setEditedOverview(o => ({ ...o, required_skills: [...(o.required_skills || []), s] }));
                        setNewSkill('');
                      }
                    }}>+ Add</button>
                  </div>
                </div>

                {/* Job Description */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 4 }}>Job Description</label>
                  <RichTextEditor
                    value={editedOverview.job_description || ''}
                    onChange={html => setEditedOverview(o => ({ ...o, job_description: html }))}
                    minHeight={200}
                    maxHeight={320}
                  />
                </div>
              </div>
            )}
          </div>

          {/* ── Pipeline Stats Card ── */}
          <div className="card">
            <div className="section-title">Pipeline Stats</div>
            {[
              ['Total Candidates', candidates.length, 'var(--accent)'],
              ['Resume Scored', scored.length, 'var(--purple)'],
              [`Passed Threshold (${threshold}+)`, passedThreshold.length, 'var(--green)'],
              ['Strong Match (75+)', candidates.filter(c => c.resume_score >= 75).length, 'var(--green)'],
              ['Good Match (55-74)', candidates.filter(c => c.resume_score >= 55 && c.resume_score < 75).length, 'var(--yellow)'],
              ['Scheduled', candidates.filter(c => c.interview_status === 'Scheduled').length, 'var(--accent)'],
              ['Interviewed', candidates.filter(c => c.interview_status === 'Completed').length, 'var(--orange)'],
              ['Selected', candidates.filter(c => c.shortlisted).length, 'var(--green)'],
            ].map(([label, val, color]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{label}</span>
                <span style={{ fontWeight: 700, fontSize: 16, color, fontFamily: 'var(--font-mono)' }}>{val}</span>
              </div>
            ))}

            {/* Score weight indicator — always reads live thresholds state */}
            <div style={{ marginTop: 16, background: 'var(--bg-hover)', borderRadius: 8, padding: '12px 14px', fontSize: 12, color: 'var(--text-secondary)' }}>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Score Weights</div>
              <div style={{ height: 8, borderRadius: 4, overflow: 'hidden', background: 'var(--border)', marginBottom: 8 }}>
                <div style={{ height: '100%', display: 'flex' }}>
                  <div style={{ width: `${Math.round((thresholds.resume_weight ?? 0.4) * 100)}%`, background: 'var(--accent)', transition: 'width 0.4s' }} />
                  <div style={{ flex: 1, background: 'var(--purple)', opacity: 0.7 }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Resume: <strong style={{ color: 'var(--accent)' }}>{Math.round((thresholds.resume_weight ?? 0.4) * 100)}%</strong></span>
                <span>Interview: <strong style={{ color: 'var(--purple)' }}>{Math.round((thresholds.interview_weight ?? 0.6) * 100)}%</strong></span>
              </div>
            </div>

            {/* Quick link to Pipeline Settings */}
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setTab('settings')} style={{ fontSize: 11 }}>
                <SettingsIcon size={12} strokeWidth={2} /> Edit Weights in Pipeline Settings <ArrowRight size={12} strokeWidth={2} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── CANDIDATES ─── */}
      {tab === 'candidates' && (
        <div>
          {/* Bulk actions bar */}
          {mayRecruit && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, gap: 8 }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setConfirmArchive(true)}
              disabled={!jobActive || archiving}
              title={jobLockHint}
              style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
            >
              {archiving ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Archiving...</> : `Archive Below ${threshold}`}
            </button>
          </div>}

          {confirmArchive && (
            <div className="modal-backdrop" onClick={() => setConfirmArchive(false)} role="presentation">
              <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
                <h3>Archive below {threshold}?</h3>
                <div className="modal-sub">
                  {candidates.filter(c => c.resume_score != null && c.resume_score < threshold).length} candidate(s)
                  scored under {threshold} will be moved to the archive. Nothing is deleted — you can restore any of
                  them from their profile.
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn" onClick={() => setConfirmArchive(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={handleBulkArchive} disabled={archiving}>
                    {archiving ? 'Archiving…' : 'Archive them'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <RankedCandidates candidates={candidates} threshold={threshold} />
        </div>
      )}

      {/* ─── QUESTIONS ─── */}
      {/* ─── SHARE & EMBED ─── */}

      {/* ─── PIPELINE SETTINGS ─── */}
      {tab === 'settings' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Score Thresholds */}
          <div className="card">
            <div className="section-title">Pipeline Pass Thresholds</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              Configure the minimum scores required to advance candidates through each stage.
            </div>

            {[
              {
                key: 'resume_score_threshold',
                label: 'Resume Score Threshold',
                desc: 'Candidates who score at or above this mark advance to the interview stage.',
                min: 0, max: 100, step: 5,
              },
              {
                key: 'interview_score_threshold',
                label: 'Interview Score Threshold',
                desc: 'Candidates who score at or above this mark are added to the shortlist.',
                min: 0, max: 100, step: 5,
              },
            ].map(({ key, label, desc, min, max, step }) => (
              <div key={key} style={{ marginBottom: 22 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <label style={{ fontWeight: 600, fontSize: 13.5 }}>{label}</label>
                  <span style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 18,
                    fontWeight: 700,
                    color: SCORE_COLOR(thresholds[key]),
                    minWidth: 40,
                    textAlign: 'right',
                  }}>
                    {thresholds[key]}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>{desc}</div>
                <input
                  type="range"
                  min={min} max={max} step={step}
                  value={thresholds[key]}
                  onChange={e => setThresholds(t => ({ ...t, [key]: parseInt(e.target.value) }))}
                  disabled={!mayRecruit}
                  style={{ width: '100%', accentColor: 'var(--accent)' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  <span>{min}</span><span>{max}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Score Weights */}
          <div className="card">
            <div className="section-title">Final Score Weights</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              Configure how resume and interview scores are weighted in the final candidate score. Total must equal 100%.
            </div>
            <div style={{ marginBottom: 22 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label style={{ fontWeight: 600, fontSize: 13.5 }}>Resume Score Weight</label>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 700, color: 'var(--accent)', minWidth: 50, textAlign: 'right' }}>
                  {Math.round((thresholds.resume_weight ?? 0.4) * 100)}%
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                Percentage of the final score derived from the resume/skills analysis.
              </div>
              <input
                type="range"
                min={10} max={90} step={5}
                value={Math.round((thresholds.resume_weight ?? 0.4) * 100)}
                onChange={e => {
                  const rw = parseInt(e.target.value) / 100;
                  setThresholds(t => ({ ...t, resume_weight: rw, interview_weight: parseFloat((1 - rw).toFixed(2)) }));
                }}
                disabled={!mayRecruit}
                style={{ width: '100%', accentColor: 'var(--accent)' }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label style={{ fontWeight: 600, fontSize: 13.5 }}>Interview Score Weight</label>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 700, color: 'var(--purple)', minWidth: 50, textAlign: 'right' }}>
                  {Math.round((thresholds.interview_weight ?? 0.6) * 100)}%
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                Automatically set to complement Resume Weight (always sums to 100%).
              </div>
              <div className="score-bar-track" style={{ height: 10, borderRadius: 5, overflow: 'hidden' }}>
                <div style={{ height: '100%', display: 'flex' }}>
                  <div style={{ width: `${Math.round((thresholds.resume_weight ?? 0.4) * 100)}%`, background: 'var(--accent)', transition: 'width 0.2s' }} />
                  <div style={{ flex: 1, background: 'var(--purple)', opacity: 0.7 }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                <span style={{ color: 'var(--accent)' }}>Resume {Math.round((thresholds.resume_weight ?? 0.4) * 100)}%</span>
                <span style={{ color: 'var(--purple)' }}>Interview {Math.round((thresholds.interview_weight ?? 0.6) * 100)}%</span>
              </div>
            </div>
          </div>

          {mayRecruit && <div>
            <button className="btn btn-primary" onClick={handleSaveThresholds} disabled={savingThresholds}>
              {savingThresholds ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Saving…</> : 'Save Pipeline Settings'}
            </button>
          </div>}
        </div>
      )}

    </div>
  );
}
